import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { Memory, Add, UnfoldMore, Save } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { equiposAPI, centrosAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import { textMatch } from '../../../shared/utils/textUtils';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;
const EMPTY = { equipo: '', idCentro: '', tipo: '', modelo: '', origen: '', marca: '', canales: '', multiCanal: false };

const TIPOS = [{ value: 'R', label: 'Registrar' }, { value: 'C', label: 'CPAP' }];
const MARCAS = [
  { value: '1', label: 'AKONIC' },
  { value: '2', label: 'BMC' },
  { value: '3', label: 'Philips Respironics' },
  { value: '4', label: 'ApneaLink' },
];

const GrillaEquipos = () => {
  const [equipos, setEquipos] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(equipos);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, cRes] = await Promise.all([
        equiposAPI.getEquipos(),
        centrosAPI.getCentros(),
      ]);
      setEquipos(eRes.data || []);
      setCentros(cRes.data || []);
    } catch {
      setToast({ message: 'Error al cargar los datos.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = sortedData.filter(e =>
    textMatch(e.equipo, buscar)
  );
  const totalPages = Math.ceil(filtrados.length / PAGE_SIZE);
  const paginados = filtrados.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const esDuplicado = () => {
    if (!form.equipo?.trim() || !form.idCentro) return false;
    if (editando) return false;
    return equipos.some(e =>
      e.equipo?.toLowerCase() === form.equipo.trim().toLowerCase() &&
      String(e.idCentro) === form.idCentro &&
      String(e.marca ?? '') === (form.marca ?? '')
    );
  };

  const marcaLabel = (val) => MARCAS.find(m => m.value === String(val))?.label || '';
  const tipoLabel = (val) => TIPOS.find(t => t.value === val)?.label || val;

  const handleNuevo = () => { setEditando(null); setForm(EMPTY); setMostrarForm(true); };
  const handleEditar = (e) => {
    setEditando(e);
    setForm({
      equipo: e.equipo || '',
      idCentro: e.idCentro != null ? String(e.idCentro) : '',
      tipo: e.tipo || '',
      modelo: e.modelo || '',
      origen: e.origen || '',
      marca: e.marca != null ? String(e.marca) : '',
      canales: e.canales != null ? String(e.canales) : '',
      multiCanal: e.multiCanal || false,
    });
    setMostrarForm(true);
  };
  const handleCancelar = () => { setMostrarForm(false); setEditando(null); setForm(EMPTY); };

  const handleGuardar = async (ev) => {
    ev.preventDefault();
    setLoading(true);
    try {
      const payload = {
        equipo: form.equipo.trim(),
        idCentro: Number(form.idCentro),
        tipo: form.tipo,
        modelo: form.modelo.trim(),
        origen: form.origen.trim(),
        marca: form.marca ? Number(form.marca) : null,
        canales: form.canales ? Number(form.canales) : null,
        multiCanal: form.multiCanal,
      };
      if (editando) {
        await equiposAPI.actualizar(editando.idEquipo, payload);
        setToast({ message: 'Equipo actualizado con éxito.', type: 'success' });
      } else {
        await equiposAPI.guardar(payload);
        setToast({ message: 'Equipo guardado con éxito.', type: 'success' });
      }
      handleCancelar();
      cargar();
    } catch {
      setToast({ message: 'Error al guardar el equipo.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await equiposAPI.eliminar(confirmEliminar);
      setToast({ message: 'Equipo eliminado con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar el equipo.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  const canSave = form.equipo.trim() && form.idCentro && form.tipo && form.modelo.trim() && form.origen.trim() && !esDuplicado();

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Memory sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Equipos
          </h2>
          <span className="page-subtitle">Administración de equipos</span>
        </div>

        <Modal show={mostrarForm} onHide={handleCancelar}>
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Equipo' : 'Nuevo Equipo'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>* Equipo</Form.Label>
                <Form.Control type="text" maxLength={100} placeholder="Equipo..." value={form.equipo}
                  onChange={(e) => setForm(f => ({ ...f, equipo: e.target.value }))} required autoFocus
                  isInvalid={esDuplicado()} />
                <Form.Control.Feedback type="invalid">Ya existe un equipo con esa combinación de nombre, centro y marca.</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Centro</Form.Label>
                <Form.Select key={`centro-${form.idCentro}`} value={form.idCentro}
                  onChange={(e) => setForm(f => ({ ...f, idCentro: e.target.value }))} required>
                  <option value="">Seleccione...</option>
                  {centros.map(c => <option key={c.idCentro} value={String(c.idCentro)}>{c.centro1 || c.centro}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Marca</Form.Label>
                <Form.Select key={`marca-${form.marca}`} value={form.marca}
                  onChange={(e) => setForm(f => ({ ...f, marca: e.target.value }))}>
                  <option value="">Seleccione...</option>
                  {MARCAS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Uso del equipo</Form.Label>
                <Form.Select key={`tipo-${form.tipo}`} value={form.tipo}
                  onChange={(e) => setForm(f => ({ ...f, tipo: e.target.value }))} required>
                  <option value="">Seleccione...</option>
                  {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Modelo</Form.Label>
                <Form.Control type="text" maxLength={200} placeholder="Modelo..." value={form.modelo}
                  onChange={(e) => setForm(f => ({ ...f, modelo: e.target.value }))} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Origen</Form.Label>
                <Form.Control type="text" maxLength={200} placeholder="Origen..." value={form.origen}
                  onChange={(e) => setForm(f => ({ ...f, origen: e.target.value }))} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Canales</Form.Label>
                <Form.Control type="number" min={0} placeholder="Canales..." value={form.canales}
                  onChange={(e) => setForm(f => ({ ...f, canales: e.target.value }))} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check type="checkbox" label="Multi Canal (4 y 21)" checked={form.multiCanal}
                  onChange={(e) => setForm(f => ({ ...f, multiCanal: e.target.checked }))} />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !canSave}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">Equipo</label>
                <input type="text" className="filter-input" placeholder="Buscar equipo..."
                  value={buscar} onChange={(e) => { setBuscar(e.target.value); setPage(1); }} />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => setPage(1)} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setBuscar(''); setPage(1); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{filtrados.length} equipos encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo Equipo
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idEquipo')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idEquipo') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('equipo')} style={{ cursor: 'pointer' }}>
                    Equipo {getSortIcon('equipo') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('centro')} style={{ cursor: 'pointer' }}>
                    Centro {getSortIcon('centro') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('tipo')} style={{ cursor: 'pointer' }}>
                    Uso {getSortIcon('tipo') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('modelo')} style={{ cursor: 'pointer' }}>
                    Modelo {getSortIcon('modelo') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('origen')} style={{ cursor: 'pointer' }}>
                    Origen {getSortIcon('origen') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Marca</th>
                  <th>Canales</th>
                  <th>Multi Canal</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr><td colSpan="10" className="empty-state">{loading ? 'Cargando...' : 'No hay equipos para mostrar'}</td></tr>
                ) : (
                  paginados.map((e) => (
                    <tr key={e.idEquipo}>
                      <td>{e.idEquipo}</td>
                      <td>{e.equipo}</td>
                      <td>{e.centro}</td>
                      <td>{tipoLabel(e.tipo)}</td>
                      <td>{e.modelo}</td>
                      <td>{e.origen}</td>
                      <td>{marcaLabel(e.marca)}</td>
                      <td>{e.canales}</td>
                      <td>{e.multiCanal ? 'Sí' : 'No'}</td>
                      <td className="action-cell">
                        <RowActions onEdit={() => handleEditar(e)} onDelete={() => setConfirmEliminar(e.idEquipo)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />
      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar equipo"
        message="¿Estás seguro que querés eliminar este equipo?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaEquipos;
